import Button from '@/components/basicComponents/button';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { MainWrap } from '@/styles/common';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SettingsContainer, SettingsItem, SettingsItemButtonWrapper, SettingsItemTitle, SettingsWrapper } from '..';
import { Tenants } from '../tenants';
import { superAdminItems } from './constants';
import Failure from './modals/failure';
import Success from './modals/success';
import Updating from './modals/updating';
import { UPDATE_AGGREGATE_COMMITMENT_MUTATION, UPDATE_AGGREGATE_INTEREST_MUTATION, UPDATE_CALCULATIONS_MUTATION } from './queries';

interface SuperAdminSettingsProps {
  handleCloseSuperAdminSettings: () => void;
  backToTitle?: string;
}

export const SuperAdminSettings = ({ handleCloseSuperAdminSettings, backToTitle }: SuperAdminSettingsProps) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const action = searchParams.get('action') || 'default';
  const type = searchParams.get('type') || 'default';

  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'failure' });
  const [chosenUpdateOption, setChosenUpdateOption] = useState<string | null>(null);

  const [runUpdateCalculations, { loading: updateCalculationsLoading }] = useMutation(UPDATE_CALCULATIONS_MUTATION);
  const [runUpdateAggregateCommitment, { loading: updateAggregateCommitmentLoading }] = useMutation(UPDATE_AGGREGATE_COMMITMENT_MUTATION);
  const [runUpdateAggregateInterest, { loading: updateAggregateInterestLoading }] = useMutation(UPDATE_AGGREGATE_INTEREST_MUTATION);

  const getCurrentUpdateMutation = (item: string | null) => {
    if (!item) return null;

    switch (item) {
      case superAdminItems[0]:
        return runUpdateCalculations;
      case superAdminItems[1]:
        return runUpdateAggregateCommitment;
      case superAdminItems[2]:
        return runUpdateAggregateInterest;
      default:
        return null;
    }
  };

  const runUpdate = (item: string | null) => {
    const runUpdateMutation = getCurrentUpdateMutation(item);
    if (!runUpdateMutation) return;

    setChosenUpdateOption(item);
    setModalWindow({ isOpen: true, type: 'updating' });
    runUpdateMutation({
      onCompleted: () => {
        setModalWindow({ isOpen: true, type: 'success' });
        setChosenUpdateOption(null);
      },
      onError: () => {
        setModalWindow({ isOpen: true, type: 'failure' });
      }
    });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const handleRedirect = (path: string) => {
    navigate(`/settings?action=${action}&type=${path}`);
  };

  const handleClose = () => {
    navigate(`/settings?action=${action}`);
  };

  return (
    <>
      {type === 'tenants' && <Tenants handleClose={handleClose} backToTitle="Super Admin" />}
      {type === 'default' && (
        <>
          {modalWindow.type === 'updating' && <Updating isOpen={modalWindow.isOpen} />}
          {modalWindow.type === 'success' && <Success isOpen={modalWindow.isOpen} onClose={closeModalWindow} />}
          {modalWindow.type === 'failure' && (
            <Failure isOpen={modalWindow.isOpen} onClose={closeModalWindow} retryUpdate={() => runUpdate(chosenUpdateOption)} />
          )}
          <MainWrap>
            <Header modalControl={<GoBackButton handleClose={handleCloseSuperAdminSettings} backToTitle={backToTitle} />} />
            <PageTitle title="Super Admin" />
            <SettingsContainer>
              <SettingsWrapper>
                {superAdminItems.map((item, index) => (
                  <SettingsItem key={item + index}>
                    <SettingsItemTitle id="itemTitle" onClick={() => runUpdate(item)}>
                      {item}
                    </SettingsItemTitle>
                    <SettingsItemButtonWrapper id="openButton">
                      <Button onClick={() => runUpdate(item)}>Update Now</Button>
                    </SettingsItemButtonWrapper>
                  </SettingsItem>
                ))}
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('tenants')}>
                    Advisory Firms
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('tenants')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
              </SettingsWrapper>
            </SettingsContainer>
          </MainWrap>
        </>
      )}
    </>
  );
};
